import { createGrpcWebTransport } from "@connectrpc/connect-web";
import { Code, ConnectError, type Transport } from "@connectrpc/connect";
import { secureStorage } from "../seguridad/SecureStorageService";

/** Transporte gRPC-Web único de la app (piloto: por ahora solo lo usa el
 *  login, ver CLAUDE.md, sección migración a gRPC-Web). Habla el protocolo
 *  **gRPC-Web** -no el protocolo Connect- porque el servidor expone sus
 *  servicios vía `Grpc.AspNetCore.Web` (`UseGrpcWeb()`), no vía Connect: usar
 *  `createConnectTransport` en vez de `createGrpcWebTransport` no
 *  interoperaría con ese middleware. Inyecta el header `token`, cacheado en
 *  memoria. Inicializado una vez en el bootstrap (`PlataformaInit`). */
class GrpcTransportProvider {
  private _baseRoute: string | null = null;
  private _transport: Transport | null = null;

  /** Fija la ruta base del servidor gRPC-Web. Llamar una vez en el bootstrap. */
  initBaseRoute(baseRoute: string): void {
    this._baseRoute = baseRoute;
    this._transport = null; // fuerza recrear el transporte con la nueva base
  }

  /** Token de sesión cacheado en memoria (se lee una vez de `secureStorage`). */
  private _token: string | null = null;

  /** Descarta el token cacheado: la próxima llamada lo relee de
   *  `secureStorage`. */
  clearToken(): void {
    this._token = null;
  }

  /** Qué hacer cuando el servidor rechaza la sesión con la app abierta
   *  (`Unauthenticated`: vencida, revocada, cuenta inactiva o licencia). */
  private _onSesionRechazada: (() => void) | null = null;

  /** Registra qué hacer cuando el servidor rechaza la sesión con la app
   *  abierta. Solo lo usan las marcas que entran por el portal de Nitria
   *  (redirigen a su login); sin registro, el error sigue su curso como
   *  siempre y la pantalla muestra "Debe volver a iniciar sesión". */
  onSesionRechazada(handler: () => void): void {
    this._onSesionRechazada = handler;
  }

  /** Transporte gRPC-Web único (lazy, cacheado hasta el próximo
   *  `initBaseRoute`). */
  get(): Transport {
    if (!this._baseRoute) {
      throw new Error(
        "Debe llamar GrpcTransportProvider.initBaseRoute(baseRoute: string)",
      );
    }

    return (this._transport ??= createGrpcWebTransport({
      baseUrl: this._baseRoute,
      interceptors: [
        (next) => async (req) => {
          const token = (this._token ??= await secureStorage.get());
          if (token) req.header.set("token", token);
          try {
            return await next(req);
          } catch (error) {
            if (
              this._onSesionRechazada &&
              error instanceof ConnectError &&
              error.code === Code.Unauthenticated
            ) {
              this._onSesionRechazada();
            }
            throw error;
          }
        },
      ],
    }));
  }
}

/** Instancia global del transporte gRPC-Web. */
export const grpcTransportProvider = new GrpcTransportProvider();
